<template>
  <div>
    <Header></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 ms-3 overflow-visible me-3 pt-3">
        <div class="stripe-configuration">
          <h2>Edit Match</h2>
          <p v-if="match" class="small text-muted">
            Match #{{ match.calMatchId }} &middot; {{ leagueName }}. Changes here are manual (Cal Status included, e.g. to mark a match cancelled) - they don't touch the
            match's channels or events. All times are in your local time ({{ tz }}).
          </p>

          <div v-if="loading" class="text-muted">Loading...</div>
          <div v-if="loadError !== ''" class="error-message">{{ loadError }}</div>

          <form v-if="match" @submit.prevent="save">
            <div class="form-group">
              <label for="team1">Team 1:</label>
              <input id="team1" v-model="form.team1" type="text" class="form-control" maxlength="100" style="max-width: 500px;">
            </div>
            <div class="form-group">
              <label for="team2">Team 2:</label>
              <input id="team2" v-model="form.team2" type="text" class="form-control" maxlength="100" style="max-width: 500px;">
            </div>
            <div class="form-group">
              <label for="venue">Arena:</label>
              <input id="venue" v-model="form.venue" type="text" class="form-control" maxlength="150" style="max-width: 500px;">
            </div>
            <div class="form-group">
              <label for="startLocal">Start ({{ tz }}):</label>
              <input id="startLocal" v-model="form.startLocal" type="datetime-local" class="form-control" style="max-width: 240px;">
            </div>
            <div class="form-group">
              <label for="endLocal">End ({{ tz }}) <span class="text-muted small">- optional</span>:</label>
              <input id="endLocal" v-model="form.endLocal" type="datetime-local" class="form-control" style="max-width: 240px;">
            </div>
            <div class="form-group">
              <label for="format">Format:</label>
              <select id="format" v-model="form.format" class="form-select" style="max-width: 200px;">
                <option :value="null">-</option>
                <option v-for="f in formatOptions" :key="f" :value="f">{{ f }}</option>
              </select>
            </div>

            <div class="form-group">
              <label for="calStatus">Cal Status:</label>
              <select id="calStatus" v-model="form.calStatus" class="form-select" style="max-width: 200px;">
                <option v-for="st in calStatusOptions" :key="st" :value="st">{{ st }}</option>
              </select>
            </div>

            <div v-if="error !== ''" class="error-message">{{ error }}</div>
            <br>
            <a role="button" class="procricket-btn" :class="{ disabled: saving }" @click="save">Save Changes</a>
            <a role="button" class="ms-3 procricket-btn--reverse" @click="cancel"> Cancel </a>
          </form>
          <a v-else-if="!loading" role="button" class="procricket-btn--reverse" @click="cancel"> Back </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AdminService from "@/services/admin-service";
import Header from "@/components/Header.vue";
import { fromDateTimeLocal, toDateTimeLocal, localTimezoneLabel } from "@/utils/general-functions";

/**
 * Edit Match (CalMatchEdit) - manual correction of one cal_match's teams, start/end time, arena (venue)
 * format and Cal Status, opened from All Matches or a calendar's Matches page. Saves via PUT /cal-league/match/update and goes back.
 */
export default {
  name: "CalMatchEdit",
  components: {
    Header
  },
  data() {
    return {
      tz: localTimezoneLabel(),
      match: null,
      leagueName: '',
      form: { team1: '', team2: '', venue: '', startLocal: '', endLocal: '', format: null, calStatus: '' },
      formatOptions: [],
      calStatusOptions: [],
      loading: true,
      loadError: '',
      saving: false,
      error: ''
    };
  },
  created() {
    AdminService.getCalendarStaticData().then((data) => {
      this.formatOptions = data.matchFormats || [];
      this.calStatusOptions = this.withCurrentStatus(data.calStatuses || []);
    }).catch((error) => console.error("Error loading calendar static data:", error));

    AdminService.getCalMatch(Number(this.$route.params.calMatchId)).then((match) => {
      this.match = match;
      this.form = {
        team1: match.team1 || '',
        team2: match.team2 || '',
        venue: match.venue || '',
        startLocal: toDateTimeLocal(match.startTime),
        endLocal: toDateTimeLocal(match.endTime),
        format: match.format || null,
        calStatus: match.calStatus || ''
      };
      this.calStatusOptions = this.withCurrentStatus(this.calStatusOptions);
      this.leagueName = `League #${match.calLeagueId}`;
      AdminService.getCalLeaguesList(true).then((leagues) => {
        const lg = (leagues || []).find((l) => l.calLeagueId === match.calLeagueId);
        if (lg) this.leagueName = lg.name;
      }).catch((error) => console.error("Error loading leagues:", error));
    }).catch((error) => {
      this.loadError = this.extractErrorMessage(error);
    }).finally(() => {
      this.loading = false;
    });
  },
  methods: {
    save() {
      if (this.saving || !this.match) return;
      const team1 = this.form.team1.trim();
      const team2 = this.form.team2.trim();
      const startTime = fromDateTimeLocal(this.form.startLocal);
      const endTime = fromDateTimeLocal(this.form.endLocal);
      if (!team1 || !team2) {
        this.error = 'Both teams are required.';
        return;
      }
      if (!startTime) {
        this.error = 'Start time is required.';
        return;
      }
      if (endTime && new Date(endTime) <= new Date(startTime)) {
        this.error = 'End time must be after the start time.';
        return;
      }
      this.saving = true;
      this.error = '';
      AdminService.updateCalMatch({
        calMatchId: this.match.calMatchId,
        team1,
        team2,
        startTime,
        endTime: endTime || null,
        venue: this.form.venue.trim() || null,
        format: this.form.format || null,
        calStatus: this.form.calStatus || undefined
      }).then(() => {
        this.$router.back();
      }).catch((error) => {
        this.saving = false;
        this.error = this.extractErrorMessage(error);
      });
    },
    // The match's current status must always be selectable, even if the static list lacks it.
    withCurrentStatus(options) {
      const current = this.form.calStatus;
      return current && !options.includes(current) ? [...options, current] : options;
    },
    cancel() {
      this.$router.back();
    },
    extractErrorMessage(error) {
      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return error.message || 'Something went wrong.';
    }
  }
};
</script>
<style src="../../assets/style.css">
</style>
